import { Button, Callout, Card, Container, Flex, Heading, Link, Text, TextField } from '@radix-ui/themes'
import { ExternalLinkIcon, LogInIcon, SquareAsteriskIcon, TriangleAlertIcon } from 'lucide-react'
import { FormEventHandler, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { User } from '../types/User'

import { UserProfileSelectModal } from '@/modules/users/components/UserProfileSelectModal'
import { AppVersion } from '@/modules/app/components/AppVersion'
import { useSignInFetcher } from '@/hooks/useLoginFetcher'
import { useAuth } from '@/hooks/useAuth'
import { AppPath } from '@/enums/AppPath'

export const UserSignInScreen = () => {
  const { error, isLoading, postSignInData, data } = useSignInFetcher()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [password, setPassword] = useState('')
  const [user, setUser] = useState<User>()
  const [email, setEmail] = useState('')
  const [rotationAngle, setRotationAngle] = useState(0)
  const [isHovered, setIsHovered] = useState(false)

  const cardRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { signIn } = useAuth()

  const handleLogin = async () => void postSignInData({ email, password })

  const handleOnSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    void handleLogin()
  }

  const onConfirm = (profileId: string) => {
    if (!user) {
      return
    }

    const profile = user.profiles.find((item) => item._id === profileId)

    if (profile) {
      setIsModalOpen(false)
      const returnUrl = sessionStorage.getItem('returnUrl')
      sessionStorage.removeItem('returnUrl')
      setTimeout(() => navigate(returnUrl || '/', { replace: true }), 100)
    }
  }

  const onCancel = () => navigate('/users/sign-out')

  useEffect(() => {
    if (!data) {
      return
    }

    const user = data.data

    setUser(user)

    if (user.profiles.length === 1) {
      signIn(user, user.profiles[0])
      const returnUrl = sessionStorage.getItem('returnUrl')
      sessionStorage.removeItem('returnUrl')
      navigate(returnUrl || '/', { replace: true })
    } else {
      setIsModalOpen(true)
    }
  }, [data, signIn, navigate])

  // Simple continuous rotation animation
  useEffect(() => {
    const interval = setInterval(() => {
      // Base speed: 6 degrees (3x faster than original)
      // Hovered speed: 2 degrees (same as original, calm for interaction)
      const speed = isHovered ? 2 : 6
      setRotationAngle((prev) => (prev + speed) % 360)
    }, 50) // Update every 50ms

    return () => clearInterval(interval)
  }, [isHovered])

  return (
    <Flex flexGrow="1" justify="center" align="center">
      <Container size="1" pb="9">
        <Card
          ref={cardRef}
          className="relative overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            // Performance optimizations
            willChange: 'auto',
            contain: 'layout style paint',
          }}
        >
          {/* Optimized continuously rotating border glow */}
          <div
            className="absolute inset-0 pointer-events-none z-0 rounded-[inherit]"
            style={{
              // Performance optimizations
              willChange: 'background',
              contain: 'layout style paint',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)', // Force GPU acceleration
              background: `
                  conic-gradient(from ${rotationAngle}deg at 50% 50%, 
                    transparent 0deg, 
                    rgba(255, 255, 255, 1) 20deg, 
                    rgba(255, 200, 200, 1) 40deg, 
                    rgba(255, 100, 100, 1) 60deg, 
                    rgba(220, 50, 50, 1) 80deg, 
                    transparent 100deg,
                    transparent 260deg,
                    rgba(220, 50, 50, 0.4) 280deg,
                    rgba(255, 100, 100, 0.3) 300deg,
                    rgba(255, 200, 200, 0.2) 320deg,
                    rgba(255, 255, 255, 0.3) 340deg,
                    transparent 360deg
                  )
                `,
              padding: '2px',
            }}
          >
            <div
              className="w-full h-full bg-white rounded-[inherit]"
              style={{
                // Additional performance optimizations
                willChange: 'auto',
                contain: 'layout style paint',
              }}
            />
          </div>
          <div className="relative z-10">
            <Flex p="2" gap="4" direction="column">
              <Heading>Ingresar</Heading>
              <form onSubmit={handleOnSubmit}>
                <Flex direction="column" gap="4">
                  <Flex direction="column">
                    <label htmlFor="email">Email</label>
                    <TextField.Root
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Ingresa tu email..."
                      disabled={isLoading}
                      value={email}
                      name="email"
                      type="email"
                      id="email"
                      required
                    />
                  </Flex>

                  <Flex direction="column">
                    <label htmlFor="password">Clave</label>
                    <TextField.Root
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Ingresa tu clave..."
                      disabled={isLoading}
                      value={password}
                      name="password"
                      type="password"
                      minLength={8}
                      id="password"
                      required
                    />
                  </Flex>

                  {error &&
                    (error.response?.status === 401 ? (
                      <Callout.Root color="yellow">
                        <Callout.Icon>
                          <SquareAsteriskIcon />
                        </Callout.Icon>
                        <Callout.Text>Tu email o clave no son correctos.</Callout.Text>
                      </Callout.Root>
                    ) : (
                      <Callout.Root color="red">
                        <Callout.Icon>
                          <TriangleAlertIcon />
                        </Callout.Icon>
                        <Callout.Text>{error?.message}</Callout.Text>
                      </Callout.Root>
                    ))}

                  <Button disabled={isLoading} loading={isLoading} onClick={handleLogin}>
                    <Text>Ingresar</Text>
                    <LogInIcon />
                  </Button>
                </Flex>
              </form>

              <Button disabled={isLoading} variant="ghost" onClick={() => navigate(`/${AppPath.Users}/password`)}>
                <Text>Solicitar un cambio de clave</Text>
              </Button>

              <Text align="center">
                <Link href="https://v1.veekls.com" target="_blank" size="2">
                  <Text mr="1">Ir a version anterior</Text>
                  <ExternalLinkIcon />
                </Link>
              </Text>
            </Flex>
          </div>
        </Card>
        <AppVersion />
      </Container>

      {isModalOpen && user && <UserProfileSelectModal user={user} onConfirm={onConfirm} onCancel={onCancel} />}
    </Flex>
  )
}

export default UserSignInScreen

// fix
